<template>
  <div
    id="question-page"
    v-if="surveyDetails && surveyDesign"
    :style="`background-color : ${surveyDesign.backgroundColor}`"
  >
  <div class="fixed-progress-bar"> 
    <b-progress
      class="fixed-progress-bar"
      :style="{ '--progress-color': surveyDesign.themeColor ? surveyDesign.themeColor : '#ff8828'}"
      :value="progress"
      :max="100"
      height="15px"
    ></b-progress>
  </div>
    <b-container v-if="surveyDetails">
      <b-row>
        <b-col md="9" lg="7" class="mx-auto text-center">
          <b-img
            :src="
              surveyDesign.logo
                ? surveyDesign.logo
                : require('@/assets/images/rush-logo.png')
            "
            fluid
            alt="Survey Logo"
          />
        </b-col>
      </b-row>
      <b-row>
        <b-col
          md="9"
          lg="7"
          id="survey-title"
          :style="
            `color: ${surveyDesign.titleColor}; font-size: ${
              surveyDesign.titleFontSize
            }; text-align: ${surveyDesign.titleTextAlign}; font-weight: ${
              surveyDesign.isTitleBold ? 'bold' : 'normal'
            }; text-decoration: ${
              surveyDesign.isTitleUnderline ? 'underline' : 'none'
            }; font-style: ${surveyDesign.isTitleItalic ? 'italic' : 'normal'};`
          "
        >
          {{ surveyDetails.title }}
        </b-col>
      </b-row>
      <!-- QUESTIONS START -->
      <template v-if="page">
        <b-row
          class="mb-3"
          v-for="(question) in page.questions"
          :key="question.uuid"
        >
          <b-col md="9" lg="7" class="mx-auto">
            <!--FORMS  START -->
            <ContactDetails
              v-if="question.question_type === 'contact_details'"
              :question="question"
              :answers="answers"
            />
            <MultipleChoice
              v-if="question.question_type === 'multiple_choice'"
              :question="question"
              :answers="answers"
            />
            <Checkbox
              v-if="question.question_type === 'checkbox'"
              :question="question"
              :answers="answers"
            />
            <Dropdown
              v-if="question.question_type === 'dropdown'"
              :question="question"
              :answers="answers"
            />
            <OpenEnded
              v-if="question.question_type === 'open_ended'"
              :question="question"
              :answers="answers"
            />
            <Rating
              v-if="question.question_type === 'rating'"
              :question="question"
              :answers="answers"
            />
            <FileUpload
              v-if="question.question_type === 'file_upload'"
              :question="question"
              :answers="answers"
            />
            <!--FORMS  END -->
          </b-col>
        </b-row>
      </template>
      <!-- QUESTIONS END -->
      <!-- PREVIOUS AND NEXT BUTTONS START -->
      <template v-if="currentPageIndex < surveyDetails.pages.length - 1">
        <b-row>
          <b-col md="9" lg="7" class="mx-auto text-md-right">
            <b-row>
              <b-col cols="6" lg="3" v-if="!Boolean(page.hide_prev_button)">
                <b-button
                  pill
                  class="form-control pb-3"
                  :style="
                    `
              background-color: ${surveyDesign.buttonTextColor};
              border-color: ${surveyDesign.themeColor};
              color: ${surveyDesign.themeColor};
            `
                  "
                  @click="onPreviousPage"
                >
                  Previous
                </b-button>
              </b-col>
              <b-col cols="6" lg="3" class="ml-auto">
                <b-button
                  pill
                  class="form-control pb-3"
                  :style="
                    `
              background-color: ${surveyDesign.themeColor};
              border-zcolor: ${surveyDesign.themeColor};
              color: ${surveyDesign.buttonTextColor};
            `
                  "
                  @click="onNextPage"
                >
                  Next
                </b-button>
              </b-col>
            </b-row>
          </b-col>
        </b-row>
      </template>
      <!-- PREVIOUS AND NEXT BUTTONS END -->
      <!-- PREVIOUS AND SUBMIT BUTTONS START -->
      <template v-else>
        <b-row>
          <b-col md="9" lg="7" class="mx-auto text-md-right">
            <b-row>
              <b-col cols="6" lg="3" v-if="!Boolean(page.hide_prev_button)">
                <b-button
                  pill
                  class="form-control pb-3"
                  :style="
                    `
              background-color: ${surveyDesign.buttonTextColor};
              border-color: ${surveyDesign.themeColor};
              color: ${surveyDesign.themeColor};
            `
                  "
                  @click="onPreviousPage"
                  :disabled="isLoading"
                >
                  Previous
                </b-button>
              </b-col>
              <b-col cols="6" lg="3" class="ml-auto">
                <b-button
                  pill
                  class="form-control pb-3"
                  :style="
                    `
              background-color: ${surveyDesign.themeColor};
              border-color: ${surveyDesign.themeColor};
              color: ${surveyDesign.buttonTextColor};
            `
                  "
                  @click="onSubmit"
                  :disabled="isLoading"
                >
                  <template v-if="isLoading"> Submitting... </template>
                  <template v-else> Submit </template>
                </b-button>
              </b-col>
            </b-row>
          </b-col>
        </b-row>
      </template>
      <!-- PREVIOUS AND SUBMIT BUTTONS END -->
    </b-container>
  </div>
</template>

<script>
import { mapKeys } from "lodash";
import { mapGetters, mapActions, mapMutations } from "vuex";
import ContactDetails from "./forms/ContactDetails";
import MultipleChoice from "./forms/MultipleChoice";
import Checkbox from "./forms/Checkbox";
import Dropdown from "./forms/Dropdown";
import OpenEnded from "./forms/OpenEnded";
import Rating from "./forms/Rating";
import FileUpload from "./forms/FileUpload";
import $ from "jquery";

export default {
  name: "Question",
  data() {
    return {
      progress: 0,
      currentPageIndex: 0,
      totalPages: 0,
      answers: {},
      extractedData: []
    };
  },
  components: {
    ContactDetails,
    MultipleChoice,
    Checkbox,
    Dropdown,
    OpenEnded,
    Rating,
    FileUpload
  },
  computed: {
    ...mapGetters("survey", [
      "surveyDetails",
      "surveyDesign",
      "errors",
      "isLoading"
    ]),
    ...mapGetters("auth", ["authToken"]),
    page() {
      const pages = this.surveyDetails.pages;
      if (!pages.length) return null;

      const currentPage = this.surveyDetails.pages[this.currentPageIndex];

      currentPage.questions = currentPage.questions
        .sort((a, b) => {
          return a.sort - b.sort;
        })
        .map(question => {
          question.contents = question.content
            ? JSON.parse(question.content)
            : null;
          return question;
        });
      return currentPage;
    }
  },
  methods: {
    ...mapActions("survey", ["saveSurveyAnswers"]),
    ...mapMutations("survey", ["setErrors"]),
    isAnswerFileType(data) {
      if (typeof data === "object" && data !== null) {
        if (data.name && data.base64) return true;
        else return false;
      }

      return false;
    },
    extractData() {
      for (
        let questionIndex = 0;
        questionIndex <
        this.surveyDetails.pages[this.currentPageIndex].questions.length;
        questionIndex++
      ) {
        const question = this.surveyDetails.pages[this.currentPageIndex]
          .questions[questionIndex];
        for (
          let contentIndex = 0;
          contentIndex < question.contents.length;
          contentIndex++
        ) {
          let fieldType = "";
          let gigyaQuestionId = question.gigya_question_id;
          let gigyaAnswerId = null;
          let gigyaKey = "";

          if (question.question_type == "contact_details") {
            // Extraction for Contact Details Question Type
            let attributesType =
              question.contents[contentIndex].attributes.type;
            // Define Gigya Keys based on the defined values on the API docs
            switch (attributesType) {
              case "First Name":
                gigyaKey = "firstName";
                break;
              case "Last Name":
                gigyaKey = "lastName";
                break;
              case "Email Address":
                gigyaKey = "email";
                break;
              case "Contact No.":
                gigyaKey = "mobileNumber";
                break;
              case "Birthday":
                gigyaKey = "dateOfBirth";
                break;
              case "Gender":
                gigyaKey = "gender";
                break;
              // case "preferredName":
              //   gigyaKey = "preferredName";
              //   break;
              // case "honorificPrefix":
              //   gigyaKey = "honorificPrefix";
              //   break;
              default:
                gigyaKey = "";
                break;
            }
            if (attributesType != undefined && attributesType != "Address") {
              fieldType = question.contents[contentIndex].attributes.type;
              gigyaAnswerId =
                question.contents[contentIndex].gigya_answer_id != undefined
                  ? question.contents[contentIndex].gigya_answer_id
                  : null;
              this.extractedData.push({
                questionType: question.question_type,
                fieldType: fieldType
                  .toLowerCase()
                  .replace(/ /g, "_")
                  .replace(/\./g, ""),
                uuid: question.uuid,
                gigya_qid: gigyaQuestionId,
                gigya_aid: gigyaAnswerId,
                gigya_key: gigyaKey
              });
            } else {
              for (
                let addressIndex = 0;
                addressIndex <
                question.contents[contentIndex].attributes.options.length;
                addressIndex++
              ) {
                fieldType =
                  question.contents[contentIndex].attributes.options[
                    addressIndex
                  ].type;
                this.extractedData.push({
                  questionType: question.question_type,
                  fieldType: fieldType
                    .toLowerCase()
                    .replace(/ /g, "_")
                    .replace(/\./g, ""),
                  uuid: question.uuid,
                  gigya_qid: gigyaQuestionId,
                  gigya_aid: gigyaAnswerId,
                  gigya_key: gigyaKey,
                  label: question.contents[contentIndex].attributes.options[
                    addressIndex
                  ].label
                });
              }
            }
          } else if (
            question.question_type == "multiple_choice" ||
            question.question_type == "checkbox" ||
            question.question_type == "dropdown" ||
            question.question_type == "rating"
          ) {
            // For These Question Types We Need To Extract the Gigya Answer IDs and Options, We Need To Find the Gigya Answer ID Based on the Answer

            fieldType = question.question
              .toLowerCase()
              .replace(/ /g, "_")
              .replace(/\./g, "");
            let options = question.contents[contentIndex].attributes.options;

            let gigyaAnswerIds = [];

            if (
              question.contents[contentIndex].attributes.gigya_answer_ids !=
              undefined
            ) {
              gigyaAnswerIds =
                question.contents[contentIndex].attributes.gigya_answer_ids;
            } else {
              for (
                let index = 0;
                index <
                question.contents[contentIndex].attributes.options.length;
                index++
              ) {
                gigyaAnswerIds.push({
                  gigya_answer_id: ""
                });
              }
            }

            this.extractedData.push({
              questionType: question.question_type,
              fieldType: fieldType,
              uuid: question.uuid,
              gigya_qid: gigyaQuestionId,
              options: options,
              gigya_aids: gigyaAnswerIds,
              gigya_key: gigyaKey
            });
          } else {
            fieldType = question.question
              .toLowerCase()
              .replace(/ /g, "_")
              .replace(/\./g, "");
            gigyaAnswerId =
              question.contents[contentIndex].gigya_answer_id != undefined
                ? question.contents[contentIndex].gigya_answer_id
                : null;
            this.extractedData.push({
              questionType: question.question_type,
              fieldType: fieldType,
              uuid: question.uuid,
              gigya_qid: gigyaQuestionId,
              gigya_aid: gigyaAnswerId,
              gigya_key: gigyaKey
            });
          }
        }
      }

      this.extractedData = this.extractedData.filter(
        (extractedData, index, self) =>
          index === self.findIndex(e => e.fieldType === extractedData.fieldType)
      );
    },
    validateAnswers() {
      const requiredFieldMessage = "This is a required field.";

      let errors = {};

      this.page.questions.map(question => {
        question.contents.map(content => {
          if (
            question.question_type === "contact_details" &&
            content.attributes.type === "Address"
          ) {
            content.attributes.options.map(option => {
              if (
                option.visible &&
                option.required &&
                !this.answers[option.label]
              ) {
                errors[option.label] = requiredFieldMessage;
              }
            });
          } else {
            if (content.visible && content.required) {
              if (question.question_type === "contact_details") {
                if (!this.answers[content.attributes.label === 'Enter your Birthday' ? 'Birthday' : content.attributes.label]) {
                  // console.log(content.attributes.label);
                  // console.log(this.answers[content.attributes.label]);
                  // console.log(this.answers);
                  errors[content.attributes.label] = requiredFieldMessage;
                } else {
                  if (
                    content.attributes.type === "Email Address" &&
                    !this.isValidEmail(this.answers[content.attributes.label])
                  ) {
                    errors[
                      content.attributes.label
                    ] = `Must be a valid ${content.attributes.label} `;
                  }

                  if (
                    content.attributes.type === "Contact No." &&
                    !this.isValidMobileNumber(
                      this.answers[content.attributes.label]
                    )
                  ) {
                    errors[
                      content.attributes.label
                    ] = `Must be a valid ${content.attributes.label} `;
                  }
                }
              } else {
                if (!this.answers[question.question]) {
                  errors[question.question] = requiredFieldMessage;
                }
              }
            }
          }
        });
      });

      this.setErrors(Object.keys(errors).length ? errors : null);
    },
    onPreviousPage() {
      if (!this.currentPageIndex)
        this.$router.push({
          name: "Landing",
          params: { uuid: this.surveyDetails.uuid }
        });
      else this.currentPageIndex = this.currentPageIndex - 1;
    },
    onNextPage() {
      this.validateAnswers();

      if (!this.errors) {
        this.extractData();
        this.currentPageIndex = this.currentPageIndex + 1;
      }
    },
    isValidEmail(data) {
      const emailRegex = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/; //eslint-disable-line

      return emailRegex.test(data);
    },
    isValidMobileNumber(data) {
      return /^09\d{9}$/.test(data);
    },
    onSubmit() {
      this.validateAnswers();
      if (!this.errors) {
        const questions = [];

        this.extractData();

        // for (let index = 0; index < extractedUuids.length; index++) {
        //   this.answers["questions." + index + ".uuid"] = extractedUuids[index];
        // }

        mapKeys(this.answers, (answer, label) => {
          let type = "string";

          if (this.isAnswerFileType(answer)) {
            answer = answer.base64;
            type = "file";
          }
          
          // for rating: extract the label of the answer
          if(typeof answer === 'object'
            && Object.keys(answer).includes('value')
            && Object.keys(answer).includes('label')
          ) {
            answer = answer.label;
          }

          questions.push({
            label,
            answer,
            type
          });
        });

        for (let index = 0; index < questions.length; index++) {
          const element = questions[index];
          // const elementUuid = questions[index].uuid;
          const elementLabel = questions[index].label
            .toLowerCase()
            .replace(/ /g, "_")
            .replace(/\./g, "");

          if (
            this.extractedData.find(x => x.fieldType === elementLabel) !==
            undefined
          ) {
            let specificData = this.extractedData.find(
              x => x.fieldType === elementLabel
            );
            element["fieldType"] = specificData.fieldType;
            element["uuid"] = specificData.uuid;
            element["gigya_qid"] = specificData.gigya_qid;
            element["gigya_key"] = specificData.gigya_key;

            if (
              specificData.questionType !== "multiple_choice" &&
              specificData.questionType !== "checkbox" &&
              specificData.questionType !== "dropdown" &&
              specificData.questionType !== "rating"
            ) {
              element["gigya_aid"] = this.extractedData.find(
                x => x.fieldType === elementLabel
              ).gigya_aid;
            } else {
              if (
                specificData.options.find(x => x.answer == element.answer) !==
                undefined
              ) {
                // Find the Index of the Answer and Use It To Search For the Gigya Answer ID on the gigya_aids Array on the Specific Data (Extracted Data)
                element["gigya_aid"] =
                  specificData.gigya_aids[
                    specificData.options
                      .map(function(e) {
                        return e.answer;
                      })
                      .indexOf(element.answer)
                  ].gigya_answer_id != undefined
                    ? specificData.gigya_aids[
                        specificData.options
                          .map(function(e) {
                            return e.answer;
                          })
                          .indexOf(element.answer)
                      ].gigya_answer_id
                    : null;
              } else {
                element["gigya_aid"] = null;
              }
            }
          } else {
            const dataRef = this.extractedData.find(
              x => x.label === questions[index].label
            )

            if (dataRef) element["uuid"] = dataRef.uuid;

            try {
                const questionRef = this.page.questions.find(question => {
                return question.contents.some(content => content.attributes.label === questions[index].label)
              });

              if (questionRef) element["uuid"] = questionRef.uuid;
            } catch (error) {
              console.log('failed to find question reference');
            }
          }
        }
        this.progress = 100
        this.saveSurveyAnswers({
          uuid: this.surveyDetails.uuid,
          questions
        }).then(() => {
          if (!this.errors) this.$router.push({ name: "ThankYou" });
          localStorage.clear();
        });
      }
    },
    appendCodeSnippet() {
      if (
        !this.surveyDetails ||
        (this.surveyDetails && !this.surveyDetails.pages.length)
      )
        return;

      const analyticsCode = this.surveyDetails.pages[this.currentPageIndex]
        .analytics_code;

      if (analyticsCode != null) {
        const facebookCodeSnippetScript = document.getElementById(
          "facebook-code-snippet-script"
        );
        const facebookCodeSnippetNoscript = document.getElementById(
          "facebook-code-snippet-noscript"
        );

        if (facebookCodeSnippetScript) facebookCodeSnippetScript.remove();
        if (facebookCodeSnippetNoscript) facebookCodeSnippetNoscript.remove();

        if (
          analyticsCode[0].fb_pixel_id != "" &&
          analyticsCode[0].fb_pixel_id
        ) {
          let facebookScript =
            "!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window, document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init', '" +
            analyticsCode[0].fb_pixel_id +
            "'); fbq('track', 'PageView');";
          let facebookNoScript =
            "<noscript id='facebook-code-snippet-noscript'><img height='1' width='1' style='display:none' src='https://www.facebook.com/tr?id=" +
            analyticsCode[0].fb_pixel_id +
            "&ev=PageView&noscript=1'/></noscript>";

          const appendScript = document.createElement("script");

          appendScript.setAttribute("id", "facebook-code-snippet-script");

          appendScript.append(facebookScript);

          document.head.appendChild(appendScript);
          $("head").append(facebookNoScript);
        }

        const aamScriptSnippetScript = document.getElementById(
          "aam-code-snippet"
        );

        if (aamScriptSnippetScript) aamScriptSnippetScript.remove();

        if (analyticsCode[1].aam_code != "" && analyticsCode[1].aam_code) {
          let aamScript =
            "<img id='aam-code-snippet' width='0' height='0' src='" +
            analyticsCode[1].aam_code +
            "'/>";
          $("body").append(aamScript);
        }

        const googleFirstScript = document.getElementById(
          "google-first-script"
        );

        const googleSecondScript = document.getElementById(
          "google-second-script"
        );

        if (googleFirstScript) googleFirstScript.remove();
        if (googleSecondScript) googleSecondScript.remove();

        if (
          analyticsCode[2].google_code != "" &&
          analyticsCode[2].google_code
        ) {
          const googleScript = document.createElement("script");
          googleScript.setAttribute(
            "src",
            "https://www.googletagmanager.com/gtag/js?id=" +
              analyticsCode[2].google_code
          );
          googleScript.setAttribute("id", "google-first-script");

          let secondGoogleScript =
            "window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);} gtag('js', new Date());  gtag('config', '" +
            analyticsCode[2].google_code +
            "');";

          document.head.appendChild(googleScript);

          const appendScript = document.createElement("script");

          appendScript.setAttribute("id", "google-second-script");

          appendScript.append(secondGoogleScript);

          document.head.appendChild(appendScript);
        }
      }
    }
  },
  mounted() {
    this.appendCodeSnippet();
  },
  watch: {
    currentPageIndex() {
      this.appendCodeSnippet();
      this.progress = (this.currentPageIndex / this.surveyDetails.pages.length) * 100;
      // console.log("progress", this.progress)
    },
    surveyDetails() {
      if (this.surveyDetails) {
        if (!!this.surveyDetails.is_login_required && !this.authToken)
          this.$router.push({ name: "SignUp" });
        else this.appendCodeSnippet();
      }
    }
  }
};
</script>

<style scoped>

.fixed-progress-bar {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 9999;
  border-radius: 0px !important;
}

.fixed-progress-bar >>> .progress-bar {
  background-color: var(--progress-color) !important;
  border-top-right-radius: 10px;
  border-bottom-right-radius: 10px;
}
</style>
